import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Project, ProjectColumn, ProjectTask } from '../../services/projectService';
import { useAuth } from '../../context/AuthContext';
import { canOpenProjectTask } from './projectTaskAccess';
import StageActions, { StageStarIcon } from './ProjectStageActions';
import { AssigneeAvatar, assigneesOf, dependencyLabel, formatTaskDueDate } from './projectTaskDisplay';

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`stage-chevron${expanded ? ' is-open' : ''}`}
  >
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const useCollapsedStages = (projectId: string) => {
  const key = `projectListCollapsed:${projectId}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch {
      return {};
    }
  });

  const toggle = (columnId: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [columnId]: !prev[columnId] };
      sessionStorage.setItem(key, JSON.stringify(next));
      return next;
    });
  };

  return { isCollapsed: (columnId: string) => !!collapsed[columnId], toggle };
};

interface Props {
  project: Project;
  tasks: ProjectTask[];
  canManage: boolean;
  onOpenTask: (task: ProjectTask) => void;
  onAddStage: () => void;
  onNewTask: () => void;
  onReorderStage: (columnId: string, direction: 'up' | 'down') => void;
  onDeleteStage: (columnId: string) => void;
  onSetFinished: (columnId: string) => void;
}

const TaskRow: React.FC<{
  task: ProjectTask;
  canOpen: boolean;
  onOpen: () => void;
}> = ({ task, canOpen, onOpen }) => {
  const people = assigneesOf(task);
  const pct = task.completionPercent || 0;

  return (
    <button
      type="button"
      className={`task-list-row${task.hasUnreadActivity ? ' has-activity' : ''}${canOpen ? '' : ' is-locked'}`}
      onClick={canOpen ? onOpen : undefined}
      title={canOpen ? undefined : 'You are not assigned to this task'}
    >
      <div className="task-list-cell task-list-name">
        <span className="task-check" />
        <span>
          <span className="task-list-title">
            {task.hasUnreadActivity && <span className="activity-dot" title="New activity" />}
            {task.title}
          </span>
        </span>
      </div>
      <div className="task-list-cell task-list-muted">{dependencyLabel(task)}</div>
      <div className="task-list-cell">
        {people.length ? (
          <span className="list-avatars">
            {people.map((person) => (
              <AssigneeAvatar key={person._id} user={person} />
            ))}
          </span>
        ) : (
          <span className="task-list-muted">—</span>
        )}
      </div>
      <div className="task-list-cell task-list-muted">{formatTaskDueDate(task.dueDate)}</div>
      <div className="task-list-cell">
        <span className={`priority-pill priority-${task.priority}`}>{task.priority}</span>
      </div>
      <div className="task-list-cell">
        <span className="list-progress">
          <span className="list-progress-track">
            <span style={{ width: `${pct}%` }} />
          </span>
          <span>{pct}%</span>
        </span>
      </div>
    </button>
  );
};

const StageHeader: React.FC<{
  column: ProjectColumn;
  index: number;
  total: number;
  count: number;
  finished: boolean;
  expanded: boolean;
  canManage: boolean;
  showSetFinished: boolean;
  onToggle: () => void;
  onReorder: (direction: 'up' | 'down') => void;
  onDelete: () => void;
  onSetFinished: () => void;
}> = ({
  column,
  index,
  total,
  count,
  finished,
  expanded,
  canManage,
  showSetFinished,
  onToggle,
  onReorder,
  onDelete,
  onSetFinished
}) => (
  <div className={`stage-header${index === 0 ? ' is-first' : ''}`}>
    <button type="button" className="stage-toggle" onClick={onToggle}>
      <ChevronIcon expanded={expanded} />
      <strong>{column.title}</strong>
      {finished && (
        <span className="stage-star" title="Finished stage">
          <StageStarIcon filled />
        </span>
      )}
      <span className="stage-count">{count}</span>
    </button>
    {canManage && (
      <StageActions
        index={index}
        total={total}
        showSetFinished={showSetFinished}
        onReorder={onReorder}
        onDelete={onDelete}
        onSetFinished={onSetFinished}
      />
    )}
  </div>
);

const ProjectTaskList: React.FC<Props> = ({
  project,
  tasks,
  canManage,
  onOpenTask,
  onAddStage,
  onNewTask,
  onReorderStage,
  onDeleteStage,
  onSetFinished
}) => {
  const { user } = useAuth();
  const columns = useMemo(
    () => (project.columns || []).slice().sort((a, b) => a.order - b.order),
    [project.columns]
  );
  const tasksByColumn = useMemo(() => {
    const map: Record<string, ProjectTask[]> = {};
    columns.forEach((column) => {
      map[column.id] = tasks.filter((task) => task.status === column.id);
    });
    return map;
  }, [columns, tasks]);
  const { isCollapsed, toggle } = useCollapsedStages(project._id);

  return (
    <div>
      <div className="list-view-head">
        <h2>List View</h2>
        <div className="list-view-actions">
          {canManage && (
            <Button label="+ Add Stage" className="p-button-outlined" onClick={onAddStage} />
          )}
          <Button label="+ New Task" onClick={onNewTask} />
        </div>
      </div>

      <div className="list-sheet">
        <div className="task-list-table">
          <div className="task-list-header">
            <span>Task name</span>
            <span>Dependency</span>
            <span>Assignee</span>
            <span>Due date</span>
            <span>Priority</span>
            <span>Progress</span>
          </div>
          {columns.map((column, index) => {
            const columnTasks = tasksByColumn[column.id] || [];
            const expanded = !isCollapsed(column.id);
            return (
              <section key={column.id} className="stage-section">
                <StageHeader
                  column={column}
                  index={index}
                  total={columns.length}
                  count={columnTasks.length}
                  finished={project.finishedColumnId === column.id}
                  expanded={expanded}
                  canManage={canManage}
                  showSetFinished={!project.finishedColumnId}
                  onToggle={() => toggle(column.id)}
                  onReorder={(direction) => onReorderStage(column.id, direction)}
                  onDelete={() => onDeleteStage(column.id)}
                  onSetFinished={() => onSetFinished(column.id)}
                />
                {expanded && (
                  columnTasks.length === 0 ? (
                    <p className="stage-empty">No tasks in this stage</p>
                  ) : (
                    columnTasks.map((task) => (
                      <TaskRow
                        key={task._id}
                        task={task}
                        canOpen={canOpenProjectTask(task, user, canManage)}
                        onOpen={() => onOpenTask(task)}
                      />
                    ))
                  )
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProjectTaskList;
